import { FC, useEffect, useMemo, useState } from 'react'
import { GetServerSideProps } from 'next'
import { useRouter } from 'next/router'
import dynamic from 'next/dynamic'

import RecommendationContext from 'context/recommendationContext'
import SEOContext from 'context/SEOContext'
import LikeWrap from 'containers/like-wrap/like-wrap'
const AdminEditBtn = dynamic(() => import('components/parts/admin-edit-btn/admin-edit-btn'))
import Article from 'components/articles/article-material/article-material'
import ArticleClinic from 'components/articles/article-clinic/article-clinic'
import MainLayout from 'containers/main-layout/main-layout'
import BannerAibolit from 'components/banners/banner-aibolit/banner-aibolit'
import ArticleVideo from 'components/articles/article-video/article-video'
import ArticleVideoPlaylist from 'components/articles/article-video-playlist/article-video-playlist'
import SEOMetatags from 'components/parts/seo-metatags/seo-metatags'
import SEOArticle from 'components/parts/seo-metatags/seo-article-material'
import SEOBreadCrumbs from 'components/parts/seo-metatags/seo-breadcrumbs'
import Auth from 'store/auth'
import useRecommendation from 'hooks/useRecomendation'
import useSetView from 'hooks/useSetView'

import { getAxiosSingle, postAxiosSingle } from 'pages/api/axios'
import nestRequest from 'utils/server-side/nestRequest'
import { ArticleBC } from 'constants/SEO-breadcrumbs'
import { go404 } from 'utils/redirect'
import { generateTumbnail, generateStaticPreview } from 'utils/generatePreview'
import { getDigitFromString, linkWithTranslation } from 'utils/getURLData'
import { AnalyticArticlePlace } from 'constants/analytic-data'
import { URL } from 'constants/SEO'
import { BASE_URL } from 'constants/url'
import { createQueryUnAuth } from 'components/webinar/webinar-active/player/video/utils'
import { Pathname, TypeCodes, UTMCampaignArticleType } from 'constants/data'
import { IMaterial } from 'constants/types/webinar.types'
import { INumObj } from 'constants/types/common.types'
import { IArticle } from 'components/articles/article-material/article-material.type'
import { IMaterialCard } from 'constants/types/card.types'

const AdminPath: INumObj = {
  1: 'createArticle',
  2: 'createVideo',
  3: 'сreateGuidelines',
}
interface ArticlePage {
  articleData: IMaterial
}

const ArticlePage: FC<ArticlePage> = ({ articleData }) => {
  const [article, setArticle] = useState<IMaterial>(articleData)
  const [playlist, setPlaylist] = useState<IMaterialCard[]>([])
  const { seoTitle, videoLink, previewName, ArticleTypeId } = article
  const [SEODescription, setSEODescription] = useState(article.seoDescription)

  const { isAdmin, fakeAuth } = Auth.useAuth()
  const router = useRouter()

  const { dopId, origin } = router.query
  const recommendData = useRecommendation(article.id, 'article', 4)
  const isVideoproject = Boolean(articleData.playlistId?.length)

  const SEOData = useMemo(() => {
    const title = seoTitle || article.name

    const titleVoc = {
      [TypeCodes.CLINIC]: `Клинические рекомендации - ${title} - 2025 - МКБ 10`,
      [TypeCodes.VIDEO]: `Лекция: ${title}`,
      [TypeCodes.ARTICLE]: title,
      [TypeCodes.CLINIC_CASE]: title,
    }
    const descriptionVoc = {
      [TypeCodes.CLINIC]: `${title} - официальные клинические рекомендации 2025 МЗ РФ`,
      [TypeCodes.VIDEO]: SEODescription,
      [TypeCodes.ARTICLE]: SEODescription,
      [TypeCodes.CLINIC_CASE]: SEODescription,
    }

    return {
      TITLE: titleVoc[ArticleTypeId],
      DESCRIPTION: descriptionVoc[ArticleTypeId],
      LINK: URL + '/' + Pathname.ARTICLE + '/' + articleData.linkTranslation,
      IMG: videoLink ? generateTumbnail(videoLink) : generateStaticPreview(URL, previewName),
      type: UTMCampaignArticleType[TypeCodes.VIDEO],
      id: article.id,
      place: AnalyticArticlePlace[article.ArticleTypeId as 1 | 2 | 3],
    }
  }, [article.id, SEODescription])

  const SEOArticleData = useMemo(
    () => ({
      name: article.name,
      createdAt: article.createdAt,
      updatedAt: article.updatedAt,
      Lecturs: article.Lecturs,
      linkTranslation: article.linkTranslation,
    }),
    [article.id]
  )

  const SEOArticleBC = useMemo(
    () =>
      ArticleBC({
        id: article.linkTranslation || '',
        name: article.name,
      }),
    [article.id]
  )

  useEffect(() => {
    if (!fakeAuth) return
    const getArticle = async () => {
      const articleAnswer = await getAxiosSingle(`${BASE_URL.ARTICLE}/${article.id}?spec`)
      setArticle(articleAnswer)
    }
    getArticle()
  }, [fakeAuth])

  useEffect(() => {
    setArticle(articleData)

    if (articleData.linkTranslation && !router.asPath.includes(articleData.linkTranslation)) {
      const preparedLink = linkWithTranslation(router.asPath, articleData.linkTranslation)
      router.replace(preparedLink, undefined, { shallow: true })
    }
  }, [articleData.id])

  useEffect(() => {
    const sendFakeStats = async () => {
      if (router.query.spec && fakeAuth) {
        await postAxiosSingle(
          createQueryUnAuth(router.query.spec),
          {},
          { materialId: article.id, materialType: article.materialType }
        )
      }
    }
    sendFakeStats()
  }, [fakeAuth])

  useEffect(() => {
    if (isVideoproject && articleData.playlistId?.length) {
      const isFromVideoproject = Boolean(dopId) && origin === 'videoproject'

      const playlistId = isFromVideoproject
        ? articleData.playlistId.find((element) => element === Number(dopId))
        : articleData.playlistId[articleData.playlistId.length - 1]

      const getWebinars = async () => {
        const webinarsData = await getAxiosSingle(
          `${BASE_URL.WEBINAR}?playlistId=${playlistId}&limit=50`
        )
        const articlesData = await getAxiosSingle(
          `${BASE_URL.ARTICLE}?playlistId=${playlistId}&limit=50`
        )

        const webinarsResult = webinarsData.status === 404 ? [] : webinarsData.rows
        const articlesResult = articlesData.status === 404 ? [] : articlesData.rows
        const currPlaylist = await getAxiosSingle(`${BASE_URL.VIDEOPROJECTS}/${playlistId}`)

        const result = [...webinarsResult, ...articlesResult]
        const orderedResult = result
          .map((material) => {
            const { id: materialId, materialType } = material
            const playlistElem = currPlaylist.list.find(
              (video: any) => video.materialId === materialId && video.materialType === materialType
            )
            return { ...material, order: playlistElem?.index }
          })
          .sort((a, b) => a.order - b.order)

        setPlaylist(orderedResult)
      }
      getWebinars()
    }
  }, [])

  useSetView({ materialId: article.id, materialType: 'article' })

  return (
    <>
      <SEOMetatags data={SEOData} />
      <SEOBreadCrumbs data={SEOArticleBC} />
      {article.ArticleTypeId == TypeCodes.ARTICLE && <SEOArticle {...SEOArticleData} />}

      <SEOContext.Provider value={SEOData}>
        <LikeWrap>
          <MainLayout
            isSmooth={article.ArticleTypeId == TypeCodes.CLINIC}
            noBtnUp={article.ArticleTypeId == TypeCodes.CLINIC}
          >
            <RecommendationContext.Provider value={recommendData}>
              {article.ArticleTypeId == TypeCodes.ARTICLE && <Article {...article} />}
              {article.ArticleTypeId == TypeCodes.CLINIC && (
                <>
                  <ArticleClinic {...article} setSEODescription={setSEODescription} />
                  <BannerAibolit />
                </>
              )}
              {article.ArticleTypeId == TypeCodes.VIDEO &&
                (article.playlistId?.length ? (
                  <ArticleVideoPlaylist dataset={article} playlistData={playlist} />
                ) : (
                  <ArticleVideo dataset={article} />
                ))}
            </RecommendationContext.Provider>
          </MainLayout>
        </LikeWrap>
      </SEOContext.Provider>

      {isAdmin && (
        <AdminEditBtn
          path={`${AdminPath[article.ArticleTypeId]}/${getDigitFromString(router.query.id as string)}`}
        />
      )}
    </>
  )
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  let articleData = {} as IArticle

  if (context?.params?.id) {
    try {
      const articleId = getDigitFromString(context.params.id as string)
      const restRet = await nestRequest(context, 'get', `/api/article/${articleId}`, {})
      articleData = restRet?.data

      if (!articleData) return go404()

      if (articleId == '1208') return go404()

      if (
        articleData.linkTranslation &&
        articleData.linkTranslation != (context.params.id as string)
      ) {
        return {
          redirect: {
            destination: `/${Pathname.ARTICLE}/${articleData.linkTranslation}`,
            statusCode: 307,
          },
        }
      }
    } catch (err) {
      console.warn(err)
      return go404()
    }
  }

  return {
    props: {
      articleData,
    },
  }
}

export default ArticlePage
